import { useContext, useEffect, useState } from 'react';
import { Button, SvgIcon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import { ListPageTemplate } from '../../../components/page-templates/list-page/ListPageTemplate';
import BackButton from '../../../components/shared/actions/BackButton';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';
import PromotionsListPageContent from './components/PromotionsListPageContent';

const ProcurementPromotionsPage = () => {
  const { t } = useTranslation('promotions');
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermissionTo } = useContext(UserContext);
  const { defaultInstanceId, defaultMarketId, defaultFiltersLoading } = useInstanceMarket();

  const canViewPromotions = hasPermissionTo(['PromotionRead', 'PromotionAdmin']);
  const canWritePromotions = hasPermissionTo(['PromotionWrite', 'PromotionAdmin']);
  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterMarket, setCurrentFilterMarket] = useState<string>('');
  const [currentFilterStatus, setCurrentFilterStatus] = useState<string>('');
  const [currentFilterCatalogue, setCurrentFilterCatalogue] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setCurrentFilterInstance(searchParams.get('companyId') || '');
    setCurrentFilterMarket(searchParams.get('customerTypeId') || '');
    setCurrentFilterStatus(searchParams.get('status') || '');
    setCurrentFilterCatalogue(searchParams.get('catalogueId') || '');
    setCurrentFilterSearchTerm(searchParams.get('searchTerm') || '');
  }, [location.search]);

  // Auto-populate from the user's default instance/market once, matching the other Procurement list pages.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !searchParams.get('customerTypeId') && !defaultFiltersLoading && defaultInstanceId && defaultMarketId) {
      setCurrentFilterInstance(defaultInstanceId);
      setCurrentFilterMarket(defaultMarketId);
      const params = new URLSearchParams();
      params.set('companyId', defaultInstanceId);
      params.set('customerTypeId', defaultMarketId);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [defaultFiltersLoading, defaultInstanceId, defaultMarketId]);

  const handleCreateNew = () => navigate('/procurement/promotions/create');

  const handleFilterChange = (
    instance: string,
    market: string,
    filters?: { status?: string; catalogueId?: string; searchTerm?: string },
  ) => {
    setCurrentFilterInstance(instance);
    setCurrentFilterMarket(market);

    const nextStatus = filters?.status ?? currentFilterStatus;
    const nextCatalogue = filters?.catalogueId ?? currentFilterCatalogue;
    const nextSearchTerm = filters?.searchTerm ?? currentFilterSearchTerm;

    if (filters) {
      setCurrentFilterStatus(nextStatus);
      setCurrentFilterCatalogue(nextCatalogue);
      setCurrentFilterSearchTerm(nextSearchTerm);
    }

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (market) params.set('customerTypeId', market);
    if (nextStatus) params.set('status', nextStatus);
    if (nextCatalogue) params.set('catalogueId', nextCatalogue);
    if (nextSearchTerm) params.set('searchTerm', nextSearchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  if (!canViewPromotions) {
    return null;
  }

  return (
    <PromotionsListPageContent
      onFilterChange={handleFilterChange}
      initialInstance={currentFilterInstance}
      initialMarket={currentFilterMarket}
      initialStatus={currentFilterStatus}
      initialCatalogue={currentFilterCatalogue}
      initialSearchTerm={currentFilterSearchTerm}
    >
      {({ filterBar, quickFilterContent, advancedFilterContent, advancedFilterActions, headlineContent, table, isInitialLoading }) => (
        <ListPageTemplate
          title={t('pageTitle')}
          subtitle={t('pageSubtitle')}
          loading={isInitialLoading}
          loadingLabel={t('loading', 'Loading...')}
          backButton={(
            <BackButton
              handleClick={() => navigate('/settings')}
              title={t('backToSettings', 'Back to Settings')}
              isLink={false}
            />
          )}
          actions={canWritePromotions && (
            <Button
              variant='primary'
              size='lg'
              startIcon={<SvgIcon component={Plus}
                inheritViewBox />}
              onClick={handleCreateNew}
            >
              {t('createPromotion')}
            </Button>
          )}
          headlineContent={headlineContent}
          filterContent={filterBar}
          quickFilterContent={quickFilterContent}
          advancedFilterContent={advancedFilterContent}
          advancedFilterActions={advancedFilterActions}
          table={table}
        />
      )}
    </PromotionsListPageContent>
  );
};

export default ProcurementPromotionsPage;
